"use client"

/**
 * QuestionComposerInspector — the right rail: format, filing, difficulty, and
 * the delivery options. Same provider as the builder, so a host that mounts one
 * gets the other for free.
 */

import * as React from "react"
import { ClassificationLeoSuggestions, useClassificationLeoCards } from "@/components/question-composer/classification-leo-suggestions"
import { Tabs, TabsContent, TabsList, TabsTrigger, TabsTriggerLabel } from "@/components/ui/tabs"
import { useCourseAssignments } from "@/hooks/use-course-assignments"
import { useCategoryDimensions, useCourseTrees, useTaggableStandards } from "@/hooks/use-category-dimensions"
import { useExamRole, useIsProgramDirector } from "@/contexts/exam-role-context"
import { can, verbOf } from "@/lib/objects"
import { classificationHref } from "@/lib/classification-nav"

import { useQuestionCategorization } from "@/hooks/use-question-categorization"

import { cn } from "@/lib/utils"
import { leadInPlainText } from "@/lib/lead-in-html"
import { LocalBanner } from "@/components/ui/banner"
import {
  CreatorHistoryCard,
  QuestionVersionTimeline,
  questionVersionByNumber,
  versionHistoryHeader,
} from "@/components/question-creator-history"
import type { QuestionPreview } from "@/lib/question-preview"
import { clarifyQuestionSheetEnhance } from "@/lib/leo-question-sheet-enhance"
import { LeoWorkStatus } from "@/components/assessment-structure/leo-work-status"
import { ExamCategoryPickerPanel } from "@/components/exam-category-picker-panel"
import { useExamCategories } from "@/hooks/use-exam-categories"
import { useQuestionDimensions } from "@/hooks/use-category-dimensions"
import {
  buildCategoryTree,
  buildDimensionLookup,
  categoriesInDimension,
  excludedForCourse,
  courseFolders,
  courseCodeOfFolderName,
  courseOfFolder,
  stampCategoryChange,
  type CategoryDimension,
  type CategoryProvenanceMap,
} from "@/lib/exam-categories"
import { NAV_USER } from "@/lib/mock/navigation"
import { CategoryField } from "@/components/question-composer/category-field"
import {
  STANDARDS_HUB_ID,
  StandardsAccordion,
  StandardsSummaryRow,
} from "@/components/question-composer/standards-field"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Tip } from "@/components/ui/tip"
import { ToggleSwitch } from "@/components/ui/toggle-switch"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { LeoApplyField } from "@/components/question-quality/leo-apply-motion"
import {
  LeoSpottedClassificationApply,
  LeoSpottedFindingsCard,
} from "@/components/question-quality/question-leo-jobs"
import { LeoSpottedLevelFields } from "@/components/question-quality/leo-spotted-level-fields"
import { LeoSpottedQueueCard } from "@/components/question-quality/leo-spotted-queue-card"
import { useLeoSpottedQueue } from "@/lib/question-quality/use-leo-spotted-queue"
import {
  notCheckedLeoSpottedCard,
  type LeoSpottedCard,
  type LeoSpottedIssueInput,
} from "@/lib/question-quality/leo-spotted-queue"
import {
  leoSpottedSubjectFromComposer,
  readLeoSpotted,
} from "@/lib/question-quality/leo-spotted-source"
import { ComposerPanelCard } from "@/components/templates/composer-panel-card"
import { CLASSIFICATION_IDENTIFIED_CHECK_ID } from "@/lib/question-quality"
import {
  AUTHORING_DEFAULT_POINTS,
  AUTHORING_INSPECTOR_GENERAL_TOGGLES,
  AUTHORING_MULTI_SELECT_SCORING_OPTIONS,
  AUTHORING_QUESTION_TYPES,
  authoringInspectorToggleVisible,
  resolveUnlimitedSelections,
  unlimitedSelectionsPolicy,
  type AuthoringMultiSelectScoring,
} from "@/lib/library-authoring"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  COMPOSER_FIELD_LABEL_CLASS,
  ComposerTagsField,
  BloomField,
  CustomClassificationField,
  CustomTreeField,
  DifficultyField,
  DiscriminationReading,
  FolderPickerControl,
  FormatPickerControl,
  InspectorSection,
  InspectorSubsectionLabel,
  InspectorToggleRow,
  MarksInputStepper,
} from "./composer-fields"
import { useClassificationTypes } from "@/components/classification/classification-types"
import { useQuestionComposerContext, useQuestionComposerInspectorSurface, useQuestionComposerPageScroll } from "./composer-context"

function ComposerHistoryNav({
  preview,
  itemLabel,
  selectedNumber,
  onSelect,
  onBack,
}: {
  preview: QuestionPreview
  itemLabel: string
  selectedNumber: number
  onSelect: (number: number) => void
  onBack: () => void
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0 text-muted-foreground"
          aria-label="Back to properties"
          onClick={onBack}
        >
          <i className="fa-light fa-arrow-left text-sm" aria-hidden="true" />
        </Button>
      </div>
      <QuestionVersionTimeline
        versions={preview.versions}
        selectedNumber={selectedNumber}
        header={versionHistoryHeader(preview, itemLabel)}
        onSelect={onSelect}
      />
    </div>
  )
}

function ComposerCategoryNav({
  value,
  onChange,
  onBack,
  scopeIds,
  dimensionId: startDimensionId,
  provenance,
  onChangeWithMeta,
  onVerify,
  canVerify,
  courseTrees,
  courseCode,
  courseId,
}: {
  /** The question's course folder, so a standard that does not apply to it is left out. */
  courseId?: string
  provenance?: CategoryProvenanceMap
  onChangeWithMeta?: (ids: string[], meta?: { aiIds?: string[] }) => void
  onVerify?: (categoryId: string) => void
  canVerify?: boolean
  courseTrees?: CategoryDimension[]
  courseCode?: string
  value: string[]
  onChange: (ids: string[]) => void
  onBack: () => void
  /** Which standard's tree to browse. Absent means course content. */
  dimensionId?: string | null
  /** The categories the chosen course teaches. Empty means all of them. */
  scopeIds?: readonly string[]
}) {
  const { categories: allCategories } = useExamCategories()
  const { dimensions: allDimensions } = useCategoryDimensions()
  const questionDimension = useQuestionDimensions()[0]
  // Course content and the program's standards are two tabs of one navigation.
  const startedOnStandards =
    startDimensionId === STANDARDS_HUB_ID ||
    Boolean(startDimensionId && startDimensionId !== questionDimension?.id)
  // Editing the categories themselves happens in Settings › Classification, for the roles the registry allows.
  const { role } = useExamRole()
  const canEditCategories = can("category", "edit", { id: "classification" }, role)
  const [tab, setTab] = React.useState<"content" | "standards">(
    startedOnStandards ? "standards" : "content",
  )
  const [openStandardId, setOpenStandardId] = React.useState<string | null>(
    startDimensionId && startDimensionId !== STANDARDS_HUB_ID && startedOnStandards
      ? startDimensionId
      : null,
  )
  const dimension = questionDimension
  const categories = React.useMemo(() => {
    const own = dimension
      ? categoriesInDimension(allCategories, dimension.id)
      : allCategories
    if (!scopeIds || scopeIds.length === 0) return own
    const keep = new Set(scopeIds)
    return own.filter((category) => keep.has(category.id))
  }, [allCategories, dimension, scopeIds])
  const tree = React.useMemo(() => buildCategoryTree(categories), [categories])
  const inScope = React.useMemo(() => {
    const ids = new Set(categories.map((category) => category.id))
    return value.filter((id) => ids.has(id))
  }, [categories, value])
  const { categorySuggestions } = useQuestionComposerContext()
  const suggestedIds = React.useMemo(
    () =>
      new Set(
        categorySuggestions
          .filter((row) => !dimension || row.dimensionId === dimension.id)
          .map((row) => row.categoryId),
      ),
    [categorySuggestions, dimension],
  )
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // The picker's own search field uses Escape to clear; leave that alone.
      if (event.key !== "Escape" || event.defaultPrevented) return
      const target = event.target as HTMLElement | null
      if (target?.matches?.("input, textarea")) return
      onBack()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onBack])
  /** Any number. The picker stays open; the back arrow returns to Properties. */
  const handleChange = (picked: string[]) => {
    const scope = new Set(categories.map((category) => category.id))
    onChange([...value.filter((id) => !scope.has(id)), ...picked])
  }
  const { assignments: navAssignments } = useCourseAssignments()
  const notForCourse = React.useMemo(
    () => excludedForCourse(allCategories, navAssignments, courseId),
    [allCategories, courseId, navAssignments],
  )
  const renderStandardTree = (standard: CategoryDimension, readOnly: boolean) => {
    const own = categoriesInDimension(allCategories, standard.id).filter((category) => !notForCourse.has(category.id))
    const ids = new Set(own.map((category) => category.id))
    const suggested = new Set(
      categorySuggestions.filter((row) => row.dimensionId === standard.id).map((row) => row.categoryId),
    )
    return (
      <ExamCategoryPickerPanel
        categories={own}
        tree={buildCategoryTree(own)}
        value={value.filter((id) => ids.has(id))}
        multiple
        readOnly={readOnly}
        suggestedIds={suggested}
        onChange={(picked) =>
          (onChangeWithMeta ?? onChange)([...value.filter((id) => !ids.has(id)), ...picked])
        }
        className="h-auto"
      />
    )
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0 text-muted-foreground"
          aria-label="Back to properties"
          onClick={onBack}
        >
          <i className="fa-light fa-arrow-left text-sm" aria-hidden="true" />
        </Button>
        <h2 className="m-0 text-sm font-semibold text-foreground">Category</h2>
        {canEditCategories ? (
          // A new tab, so the question being written stays open; Settings edits reach it at once.
          <Tip label={verbOf("category", "edit").tooltip ?? "Edit"} side="top">
            <Button type="button" variant="ghost" size="sm" className="ms-auto shrink-0" asChild>
              <a href={classificationHref(tab)} target="_blank" rel="noopener">
                <i className="fa-light fa-pen" aria-hidden="true" />
                {verbOf("category", "edit").label}
              </a>
            </Button>
          </Tip>
        ) : null}
      </div>
      {/* Leo's classification findings, one card for both tabs. */}
      <ClassificationLeoSuggestions
        value={value}
        scopeIds={scopeIds}
        courseTrees={courseTrees}
        onAdd={(id) => (onChangeWithMeta ?? onChange)([...new Set([...value, id])], { aiIds: [id] })}
        onAddMany={(ids) => (onChangeWithMeta ?? onChange)([...new Set([...value, ...ids])])}
        onOpenStandards={() => setTab("standards")}
      />
      <Tabs
        value={tab}
        onValueChange={(next) => setTab(next as "content" | "standards")}
        // The package's sticky tab band paints bg-background; here the tabs sit on the panel.
        className="flex min-h-0 flex-1 flex-col gap-2 [&_[data-slot=tabs-sticky-subheader]]:bg-transparent"
      >
        <TabsList ariaLabel="Category" variant="line" className="bg-transparent">
          <TabsTrigger value="content">
            <TabsTriggerLabel>Course content</TabsTriggerLabel>
          </TabsTrigger>
          <TabsTrigger value="standards">
            <TabsTriggerLabel>Standards</TabsTriggerLabel>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="content" className="mt-0 flex min-h-0 flex-1 flex-col gap-2">
          <ExamCategoryPickerPanel
            categories={categories}
            tree={tree}
            value={inScope}
            multiple
            suggestedIds={suggestedIds}
            onChange={handleChange}
            className="min-h-0 flex-1"
          />
        </TabsContent>
        <TabsContent value="standards" className="mt-0 flex min-h-0 flex-1 flex-col gap-2">
          <StandardsAccordion
            value={value}
            provenance={provenance ?? {}}
            onChange={(ids, meta) => (onChangeWithMeta ?? onChange)(ids, meta)}
            openId={openStandardId}
            onOpenChange={setOpenStandardId}
            renderTree={renderStandardTree}
            onVerify={onVerify}
            canVerify={canVerify}
            courseTrees={courseTrees}
            courseCode={courseCode}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}

/** Module scope: Ask Leo replaces the inspector, so component state is lost before Apply. */
const PENDING_SPOTTED_CARDS = new Map<string, { card: LeoSpottedCard; print: string }>()

export function QuestionInspectorCompactNav({
  density = "rail",
}: {
  density?: "rail" | "card"
}) {
  const {
    hasOpenReviewChecks,
    openLeoSpotted,
    openProperties,
    showLiveLeoSpotted,
  } = useQuestionComposerContext()
  const rail = density === "rail"
  const buttonClass = rail
    ? "size-9 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
    : cn(
        "size-11 shrink-0 rounded-xl border-border shadow-sm",
        "bg-[var(--secondary-panel-bg)] text-sidebar-foreground",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      )

  const leoButton = (
    <Tip label="Leo AI" side="left">
      <Button
        type="button"
        size={rail ? "icon" : "icon-lg"}
        variant={rail ? "ghost" : "outline"}
        className={buttonClass}
        aria-label={
          hasOpenReviewChecks ? "Leo AI. Review checks are open." : "Leo AI"
        }
        onClick={openLeoSpotted}
      >
        <span className="relative">
          <i
            className="fa-duotone fa-solid fa-star-christmas text-md leading-none text-brand"
            aria-hidden="true"
          />
          {hasOpenReviewChecks ? (
            <span
              className="absolute -right-1 -top-1 size-1.5 rounded-full bg-destructive"
              aria-hidden="true"
            />
          ) : null}
        </span>
      </Button>
    </Tip>
  )

  const propertiesButton = (
    <Tip label="Properties" side="left">
      <Button
        type="button"
        size={rail ? "icon" : "icon-lg"}
        variant={rail ? "ghost" : "outline"}
        className={buttonClass}
        aria-label="Properties"
        onClick={openProperties}
      >
        <i className="fa-light fa-sliders text-md leading-none" aria-hidden="true" />
      </Button>
    </Tip>
  )

  if (rail) {
    return (
      <>
        {showLiveLeoSpotted ? (
          <li className="flex w-full justify-center" role="none">
            {leoButton}
          </li>
        ) : null}
        <li className="flex w-full justify-center" role="none">
          {propertiesButton}
        </li>
      </>
    )
  }

  return (
    <div className="flex w-full flex-col items-center gap-1 py-1">
      {showLiveLeoSpotted ? leoButton : null}
      {propertiesButton}
    </div>
  )
}

export function QuestionComposerInspectorFields() {
  const {
    form,
    inspectorLead,
    leoSpottedLead,
    importLeoFixedOnly,
    inspectorPanel,
    capabilities,
    disabled,
    folders,
    assist,
    onRequestNewFolder,
    onReadyChange,
    leoJobs,
    composeSpotted,
    showLiveLeoSpotted,
    blueprintExplain,
    qualityChecks,
    qualitySnapshot,
    historyQuestionId,
    composeHistoryBankRow,
    composeHistoryPreview,
    hasSavedHistory,
    composeHistoryCurrentNumber,
    historyViewNumber,
    historyPickerOpen,
    setHistoryPickerOpen,
    selectComposeHistoryVersion,
    restoreComposeCurrentVersion,
    applyQualityCheck,
    leoWorking,
    enhanceWithLeo,
    setLeoVariationMode,
    setLeoBriefOpen,
    folderPickerOpen,
    setFolderPickerOpen,
    categoryPickerOpen,
    setCategoryPickerOpen,
    categoryPickerDimensionId,
    openCategoryPicker,
    findBlueprintMatch,
    setDifficultyMode,
    difficultyInsight,
    hideDiscrimination,
    tagDraft,
    setTagDraft,
    watchedType,
    watchedTags,
    watchedStatus,
    changeType,
    commitTag,
    removeTag,
  } = useQuestionComposerContext()

  const composerCategoryIds = form.watch("categoryIds")
  const isReady = watchedStatus === "published"
  const showMultiSelectScoring =
    watchedType === "mcq_multiple" || watchedType === "matching"

  const formatOptions = React.useMemo(
    () =>
      AUTHORING_QUESTION_TYPES.filter((option) => {
        if (option.value === watchedType) return true
        if (option.value === "numeric") return capabilities.numeric
        if (option.value === "fill_blank") return capabilities.fillBlank
        if (option.value === "matching") return capabilities.matchingPairs
        if (option.value === "ordering") return capabilities.ordering
        if (option.value === "hotspot") return capabilities.hotspot
        return true
      }),
    [capabilities, watchedType],
  )

  const visibleGeneralToggles = AUTHORING_INSPECTOR_GENERAL_TOGGLES.filter(
    (option) => authoringInspectorToggleVisible(option, watchedType),
  )
  const identifiedClassification = qualityChecks.filter(
    (check) => check.id === CLASSIFICATION_IDENTIFIED_CHECK_ID,
  )
  React.useLayoutEffect(() => {
    const leo = document.getElementById("composer-leo-spotted")
    const scroller =
      leo?.closest<HTMLElement>("[data-composer-inspector-scroll]") ?? null
    if (!scroller) return
    const pin = () => {
      scroller.scrollTop = 0
    }
    pin()
    const frame = window.requestAnimationFrame(pin)
    return () => window.cancelAnimationFrame(frame)
  }, [blueprintExplain?.title, leoJobs.length])

  const panelScrollReady = React.useRef(false)
  React.useEffect(() => {
    if (!panelScrollReady.current) {
      panelScrollReady.current = true
      return
    }
    const id =
      inspectorPanel === "leo"
        ? "composer-leo-spotted"
        : "composer-properties"
    const target = document.getElementById(id)
    const scroller = target?.closest<HTMLElement>(
      "[data-composer-inspector-scroll]",
    )
    if (!target || !scroller) return
    const top = target.offsetTop - scroller.offsetTop
    scroller.scrollTo({ top: Math.max(0, top) })
  }, [inspectorPanel])

  const openSpottedIssues = composeSpotted?.issues.filter((issue) => composeSpotted.openIds.has(issue.id)) ?? []

  /**
   * Leo spotted as a queue rather than a stacked list (PRD 2.2.1).
   *
   * The findings are the same ones the old card listed; what changes is that
   * the author meets them one at a time in pedagogical order, with Skip for
   * "not now" and Dismiss for "this is not a defect".
   */
  // Live form state, read by the same engine the bank row and the sheet
  // use, so the composer cannot say Looks good about a question the table
  // is badging Needs fixing.
  const spottedReading = readLeoSpotted(
    leoSpottedSubjectFromComposer({
      questionId: historyQuestionId,
      title: qualitySnapshot.title,
      stem: leadInPlainText(qualitySnapshot.leadIn),
      type: qualitySnapshot.type,
      options: qualitySnapshot.options,
      rationale: qualitySnapshot.rationale,
      bloom: qualitySnapshot.bloom,
      difficulty: qualitySnapshot.difficulty,
      categoryIds: qualitySnapshot.categoryIds,
      // The same finding the sheet and the tables carry, so the editor's
      // status cannot differ from the list the question was opened from.
      alignment: blueprintExplain
        ? { aligned: blueprintExplain.aligned, detail: blueprintExplain.summary }
        : null,
    }),
  )
  // Named so the dependency array below holds values rather than
  // expressions: an expression there is invisible to the compiler and
  // to anyone reading it.
  const spottedIssueIdKey = spottedReading.issues.map((issue) => issue.id).join("|")
  const snapshotCategoryCount = qualitySnapshot.categoryIds?.length ?? 0

  const spottedQueueInput = React.useMemo(
    () => {
      const merged = new Map<string, LeoSpottedIssueInput>()
      for (const issue of spottedReading.issues) {
        const live = openSpottedIssues.find((row) => row.id === issue.id)
        merged.set(
          issue.id,
          live
            ? {
                ...issue,
                outcome: issue.outcome ?? live.outcome,
                instruction: issue.instruction ?? live.instruction,
              }
            : issue,
        )
      }
      for (const live of openSpottedIssues) {
        if (merged.has(live.id)) continue
        merged.set(live.id, {
          id: live.id,
          label: live.label,
          cover: live.cover,
          outcome: live.outcome,
          instruction: live.instruction,
        })
      }
      return {
      // Shared reading plus live compose findings (blueprint alignment, checks).
      issues: [...merged.values()],
      bloom: qualitySnapshot.bloom,
      difficulty: qualitySnapshot.difficulty,
      categoryIds: qualitySnapshot.categoryIds,
      resolvedCount: spottedReading.resolvedCount,
      // Answered findings stay in the stack behind the open ones.
      resolvedIssues: spottedReading.resolvedIssues,
      edited: spottedReading.edited,
      tag:
        spottedReading.tag === "worth-a-look"
          ? ("worth-a-look" as const)
          : spottedReading.tag
            ? ("needs-fixing" as const)
            : null,
      }
    },
    // `openSpottedIssues` is rebuilt every render; its ids are the content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      spottedIssueIdKey,
      openSpottedIssues.map((issue) => issue.id).join("|"),
      spottedReading.tag,
      spottedReading.resolvedCount,
      spottedReading.resolvedIssues,
      spottedReading.edited,
      qualitySnapshot.bloom,
      qualitySnapshot.difficulty,
      snapshotCategoryCount,
    ],
  )
  const spottedQueue = useLeoSpottedQueue(
    historyQuestionId ?? "composer-draft",
    spottedQueueInput,
  )

  const queueIssueCount = spottedQueueInput.issues.length
  const notCheckedLeoSpotted =
    queueIssueCount === 0 &&
    !spottedReading.tag &&
    spottedReading.resolvedCount === 0 &&
    !spottedReading.edited
  const leoSpottedDisplayQueue = notCheckedLeoSpotted
    ? {
        cards: [notCheckedLeoSpottedCard("idle", false)],
        activeIndex: 0,
      }
    : spottedQueue.queue
  const showLeoSpottedQueueCard =
    showLiveLeoSpotted &&
    (notCheckedLeoSpotted || leoSpottedDisplayQueue.cards.length > 0)

  /**
   * A card counts as answered once the field it covers changes, not when
   * its button is pressed. Pressing only asks Leo; the author can still
   * discard the reply, and a finding recorded on the click stayed resolved
   * over an unchanged question.
   */
  const spottedFieldPrint = (dimension: LeoSpottedCard["dimension"]) => {
    const stem = leadInPlainText(qualitySnapshot.leadIn).trim()
    const choices = qualitySnapshot.options.map((option) => `${option.text}|${option.isCorrect}`).join("\n")
    switch (dimension) {
      case "stem":
        return `${stem}\n${qualitySnapshot.type}`
      case "distractors":
        return choices
      case "rationale":
        return qualitySnapshot.rationale ?? ""
      case "metadata":
        return `${qualitySnapshot.bloom}|${qualitySnapshot.difficulty}`
      case "category":
        return (qualitySnapshot.categoryIds ?? []).join(",")
      default:
        return `${stem}\n${choices}\n${qualitySnapshot.rationale ?? ""}`
    }
  }
  const pendingKey = historyQuestionId ?? "composer-draft"
  const pendingSpotted = PENDING_SPOTTED_CARDS.get(pendingKey) ?? null
  const pendingPrintNow = pendingSpotted ? spottedFieldPrint(pendingSpotted.card.dimension) : null
  React.useEffect(() => {
    if (!pendingSpotted || pendingPrintNow === pendingSpotted.print) return
    PENDING_SPOTTED_CARDS.delete(pendingKey)
    spottedQueue.resolve(pendingSpotted.card)
  }, [pendingSpotted, pendingPrintNow, pendingKey, spottedQueue])

  /** The queue's sparkle CTA, routed to the handler that card implies. */
  function runSpottedCard(card: LeoSpottedCard) {
    if (card.id === "not-checked") {
      enhanceWithLeo(
        undefined,
        "Run a quality check on this question. Report stem, choice, rationale, and classification findings I should fix.",
      )
      return
    }
    if (card.id === "assessment-blueprint-alignment") {
      if (findBlueprintMatch) {
        findBlueprintMatch()
        return
      }
      enhanceWithLeo(
        undefined,
        card.instruction ??
          "Align this question with the section blueprint. Keep the learning objective and answer format.",
      )
      return
    }
    switch (card.kind) {
      case "overuse":
        setLeoVariationMode(true)
        setLeoBriefOpen(true)
        break
      case "category":
        enhanceWithLeo(undefined, "Suggest the best category for this question. Explain why and let me review before applying.")
        break
      case "metadata":
        enhanceWithLeo(undefined, "Suggest Bloom's level and difficulty for this question. Explain why and let me review before applying.")
        break
      case "performance":
        enhanceWithLeo(
          undefined,
          "Diagnose why this question performed poorly and propose a fix I can review before applying.",
        )
        break
      default:
        // The card's own instruction names the one thing to change. The
        // fallback behind it asks for a whole-question rewrite, which is
        // what used to answer "Write rationale" with a new stem and new
        // choices and no rationale.
        enhanceWithLeo(
          undefined,
          card.instruction ??
            `Rewrite this question to resolve all open findings while keeping its learning objective and answer format: ${card.issues
              .map((issue) => issue.label)
              .join("; ")}.`,
        )
    }
    if (card.selfClearing) return
    PENDING_SPOTTED_CARDS.set(pendingKey, {
      card,
      print: spottedFieldPrint(card.dimension),
    })
  }

  const classificationMissing = openSpottedIssues.some((issue) => /classification|category|bloom|difficulty/i.test(`${issue.id} ${issue.label}`))
  const stemFinding = openSpottedIssues.some((issue) => issue.cover === "stem")
  const choiceFinding = openSpottedIssues.some((issue) => issue.cover === "choices")
  const choiceFitFinding = openSpottedIssues.some((issue) => issue.id === "choices-fit")
  const multiSpottedClarify = openSpottedIssues.length > 1 ? {
    question: "What should Leo change in this question?",
    choices: [
      { id: "all", label: "Fix all open findings", instruction: `Rewrite this question to resolve all open findings while keeping its learning objective and answer format: ${openSpottedIssues.map((issue) => issue.label).join("; ")}.` },
      ...(classificationMissing ? [{ id: "classification", label: "Suggest classification", instruction: "Suggest the best category, Bloom's level, and difficulty for this question. Explain the reasons and let me review before applying." }] : []),
      ...(stemFinding ? [{ id: "stem", label: "Change the stem only", instruction: "Tighten the stem. Leave the choices for a next step if they no longer fit." }] : []),
      ...(choiceFitFinding
        ? [{ id: "choices", label: "Harmonize the choices only", instruction: "Harmonize the choices to the new question. Keep each choice in its current letter." }]
        : choiceFinding ? [{ id: "choices", label: "Change the choices only", instruction: "Write plausible answer choices for this stem" }] : []),
    ],
  } : null

  const bloomMissing = !qualitySnapshot.bloom?.trim()
  const difficultyMissing = !qualitySnapshot.difficulty?.trim()
  const spottedLevelFields =
    bloomMissing || difficultyMissing ? (
      <LeoSpottedLevelFields
        stem={leadInPlainText(qualitySnapshot.leadIn)}
        type={qualitySnapshot.type}
        showBloom={bloomMissing}
        showDifficulty={difficultyMissing}
        disabled={disabled}
        onApply={(next) => {
          if (next.bloom) {
            form.setValue("bloom", next.bloom, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          if (next.difficulty) {
            form.setValue("difficulty", next.difficulty, {
              shouldDirty: true,
              shouldValidate: true,
            })
            setDifficultyMode("manual")
          }
          const metadata = spottedQueue.queue.cards.find(
            (card) => card.kind === "metadata" && card.state === "open",
          )
          if (metadata) spottedQueue.resolve(metadata)
        }}
        onSkip={() => {
          const metadata = spottedQueue.queue.cards.find(
            (card) => card.kind === "metadata" && card.state === "open",
          )
          if (metadata) spottedQueue.skip(metadata)
        }}
      />
    ) : null

  const historySelectedNumber = historyViewNumber ?? composeHistoryCurrentNumber
  const historyCardVersion =
    questionVersionByNumber(
      composeHistoryPreview.versions,
      historySelectedNumber,
    ) ?? composeHistoryPreview.versions[0]
  const historyItemLabel =
    composeHistoryBankRow.title?.trim() ||
    leadInPlainText(form.getValues("leadIn")).trim().slice(0, 80) ||
    "Draft question"

  /**
   * Every change to the tags goes through here, so the record of who placed
   * each one cannot drift from the tags themselves.
   */
  // Composing in the bank starts with a course; inside an assessment the
  // course is known and nothing is asked.
  const { categories: courseCategories } = useExamCategories()
  const { assignments: courseAssignments } = useCourseAssignments()
  const askForCourse = !assist
  const [coursePickerOpen, setCoursePickerOpen] = React.useState(false)
  const watchedFolderIds = form.watch("folderIds")
  const watchedCategoryIds = form.watch("categoryIds")
  const categorization = useQuestionCategorization(
    { folderIds: watchedFolderIds, categoryIds: watchedCategoryIds },
    folders,
  )
  // The categories are the course's own, whether the course was just chosen
  // or the question is being written for an assessment in that course.
  const course = categorization.course
  const courseScopeIds = React.useMemo(
    () => (course ? categorization.options.map((c) => c.id) : []),
    [categorization, course],
  )
  const courseCode = courseCodeOfFolderName(course?.name)
  const courseOwnedTrees = useCourseTrees(courseCode)
  // Settings › Classification: which types, what they are called, in what order.
  const classificationTypes = useClassificationTypes()
  const isDirectorHere = useIsProgramDirector()
  const { dimensions: allStandardDimensions } = useCategoryDimensions()
  const standardLookup = React.useMemo(
    () => buildDimensionLookup(courseCategories),
    [courseCategories],
  )
  const standardDimensionIds = React.useMemo(
    () =>
      new Set(allStandardDimensions.filter((d) => d.tier === "program").map((d) => d.id)),
    [allStandardDimensions],
  )
  const verifyStandardTag = (categoryId: string) => {
    const current = form.getValues("categoryProvenance") ?? {}
    const record = current[categoryId]
    if (!record) return
    form.setValue(
      "categoryProvenance",
      { ...current, [categoryId]: { ...record, review: "verified", by: NAV_USER.name, at: new Date().toISOString() } },
      { shouldDirty: true },
    )
  }
  const changeCategories = (ids: string[], meta?: { aiIds?: string[] }) => {
    form.setValue(
      "categoryProvenance",
      stampCategoryChange(
        form.getValues("categoryIds") ?? [],
        ids,
        form.getValues("categoryProvenance"),
        {
          by: NAV_USER.name,
          aiIds: meta?.aiIds,
          // A standard a faculty member tags waits for the director's check.
          needsReview: (id) =>
            !isDirectorHere && standardDimensionIds.has(standardLookup.get(id) ?? ""),
        },
      ),
      { shouldDirty: true },
    )
    form.setValue("categoryIds", ids, { shouldDirty: true, shouldValidate: true })
  }

  // Leo's classification findings join the one Leo spotted stack, after the
  // quality cards: one stack to page, not a card per kind of finding.
  const classificationLeo = useClassificationLeoCards({
    value: composerCategoryIds ?? [],
    scopeIds: courseScopeIds,
    courseTrees: courseOwnedTrees,
    onAdd: (id) => changeCategories([...new Set([...(composerCategoryIds ?? []), id])], { aiIds: [id] }),
    onAddMany: (ids) => changeCategories([...new Set([...(composerCategoryIds ?? []), ...ids])]),
    onOpenStandards: () => openCategoryPicker(STANDARDS_HUB_ID),
  })
  // A course comes first: until then there is nothing to suggest from.
  const classificationCards = askForCourse && !course ? [] : classificationLeo.cards
  const qualityCards = showLeoSpottedQueueCard ? leoSpottedDisplayQueue.cards : []
  const stackCards = [...qualityCards, ...classificationCards]
  const isClassificationCard = (card: LeoSpottedCard | undefined) =>
    Boolean(card && classificationCards.some((entry) => entry.id === card.id))
  // Paging inside the quality cards stays the quality queue's; past them the stack keeps its own place.
  const [classificationIndex, setClassificationIndex] = React.useState<number | null>(null)
  const stackActive = Math.min(
    classificationIndex ?? leoSpottedDisplayQueue.activeIndex,
    Math.max(stackCards.length - 1, 0),
  )

  if (historyPickerOpen) {
    return (
      <ComposerHistoryNav
        preview={composeHistoryPreview}
        itemLabel={historyItemLabel}
        selectedNumber={historySelectedNumber}
        onSelect={selectComposeHistoryVersion}
        onBack={() => setHistoryPickerOpen(false)}
      />
    )
  }

  if (categoryPickerOpen) {
    return (
      <ComposerCategoryNav
        value={composerCategoryIds ?? []}
        onChange={(ids) => changeCategories(ids)}
        onBack={() => setCategoryPickerOpen(false)}
        scopeIds={courseScopeIds}
        dimensionId={categoryPickerDimensionId}
        provenance={form.watch("categoryProvenance") ?? {}}
        onChangeWithMeta={(ids, meta) => changeCategories(ids, meta)}
        onVerify={verifyStandardTag}
        canVerify={isDirectorHere}
        courseTrees={courseOwnedTrees}
        courseCode={courseCode}
        courseId={course?.id}
      />
    )
  }

  return (
    <fieldset disabled={disabled} className="contents">
          <div id="composer-leo-spotted" className="flex flex-col gap-3">
            {leoWorking ? (
              <LeoWorkStatus label={leoWorking} />
            ) : null}
            {leoSpottedLead}
            {showLiveLeoSpotted &&
            !importLeoFixedOnly &&
            identifiedClassification[0] &&
            !composeSpotted?.issues.some((issue) => issue.cover === "category" || issue.cover === "bloom" || issue.cover === "difficulty") ? (
              <LeoSpottedClassificationApply
                check={identifiedClassification[0]}
                snapshot={qualitySnapshot}
                disabled={disabled}
                applying={Boolean(leoWorking)}
                onApply={(check, alternate) =>
                  applyQualityCheck(check, false, alternate)
                }
              />
            ) : null}
            {stackCards.length > 0 ? (
              // One stack: Leo's quality findings, then its classification findings.
              <LeoSpottedQueueCard
                queue={{ cards: stackCards, activeIndex: stackActive }}
                tag={isClassificationCard(stackCards[stackActive]) ? null : spottedReading.tag}
                onNavigate={(index) => {
                  if (index < qualityCards.length) {
                    setClassificationIndex(null)
                    if (!notCheckedLeoSpotted) spottedQueue.navigate(index)
                  } else setClassificationIndex(index)
                }}
                onPrimary={(card) => (isClassificationCard(card) ? classificationLeo.primary(card) : runSpottedCard(card))}
                onSkip={(card) => (isClassificationCard(card) ? classificationLeo.skip(card) : spottedQueue.skip(card))}
                onReopen={(card) => (isClassificationCard(card) ? classificationLeo.reopen(card) : spottedQueue.reopen(card))}
                disabled={disabled}
                levelFields={notCheckedLeoSpotted ? null : spottedLevelFields}
                fixing={Boolean(leoWorking)}
                fixingLabel={leoWorking ?? undefined}
              />
            ) : null}
          </div>
      <InspectorSection title="Properties">
      <div id="composer-properties" className="flex flex-col gap-6">
        {onReadyChange ? (
          // The label names what turning it on does; the line under it says where the question stands.
          <div className="flex items-center justify-between gap-3 border-b border-border pb-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Label id="qb-mark-ready-label" htmlFor="qb-mark-ready" className="text-sm font-medium text-foreground">
                Publish this question
              </Label>
              <p id="qb-mark-ready-hint" className="m-0 text-xs leading-snug text-muted-foreground">
                {isReady
                  ? "Published. It can go on an assessment."
                  : "Draft. Turn on to save it and make it available for assessments."}
              </p>
            </div>
            <ToggleSwitch
              id="qb-mark-ready"
              checked={isReady}
              onChange={onReadyChange}
              // Named by its label, not the switch's default "On" / "Off".
              aria-labelledby="qb-mark-ready-label"
            />
          </div>
        ) : null}
        {inspectorLead}
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="gap-1.5">
                <Label htmlFor="qb-format" className={COMPOSER_FIELD_LABEL_CLASS}>
                  Question format
                </Label>
                <FormControl>
                  <FormatPickerControl
                    id="qb-format"
                    options={formatOptions}
                    value={field.value}
                    onChange={(v) => changeType(v)}
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {askForCourse ? (
            <div className="flex flex-col gap-1.5">
              {/* The asterisk sits against the word: the label's own flex gap is too wide for it. */}
              <Label className={cn(COMPOSER_FIELD_LABEL_CLASS, "gap-0.5")}>
                Course
                <span className="text-destructive" aria-hidden="true">
                  *
                </span>
              </Label>
              {/* The same control as Locations, one course at a time. */}
              <FolderPickerControl
                folders={courseFolders(folders)}
                value={course ? [course.id] : []}
                onChange={(ids) => {
                  const courseId = ids[0]
                  if (!courseId) return
                  form.setValue("folderIds", [courseId], {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                  // A category the new course does not teach does not carry over.
                  const dropped = excludedForCourse(courseCategories, courseAssignments, courseId)
                  const current = form.getValues("categoryIds") ?? []
                  if (current.some((id) => dropped.has(id))) {
                    changeCategories(current.filter((id) => !dropped.has(id)))
                  }
                }}
                open={coursePickerOpen}
                onOpenChange={setCoursePickerOpen}
                onRequestNewFolder={() => {}}
                showNewFolder={false}
                multiple={false}
                ariaNoun="course"
                emptyLabel="Choose a course"
                emptyHint="Required. Its categories appear below."
              />
            </div>
          ) : null}
          <FormField
            control={form.control}
            name="folderIds"
            render={({ field }) => (
              <FormItem className="gap-1.5">
                <Label className={COMPOSER_FIELD_LABEL_CLASS}>Locations</Label>
                <FormControl>
                  <FolderPickerControl
                    folders={folders}
                    value={field.value}
                    onChange={(v) => field.onChange(v)}
                    open={folderPickerOpen}
                    onOpenChange={setFolderPickerOpen}
                    onRequestNewFolder={() => {
                      setFolderPickerOpen(false)
                      onRequestNewFolder?.()
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {capabilities.scoring ? (
        <InspectorSection title="Scoring">
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="points"
              render={({ field }) => (
                <FormItem className="gap-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <Label
                      htmlFor="qb-points"
                      className={cn(COMPOSER_FIELD_LABEL_CLASS, "shrink-0")}
                    >
                      Marks
                    </Label>
                    <FormControl>
                      <MarksInputStepper
                        id="qb-points"
                        value={field.value}
                        placeholder={AUTHORING_DEFAULT_POINTS}
                        disabled={disabled}
                        onChange={field.onChange}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {showMultiSelectScoring ? (
              <FormField
                control={form.control}
                name="multiSelectScoring"
                render={({ field }) => {
                  const unlimitedPolicy = unlimitedSelectionsPolicy(field.value)
                  return (
                    <FormItem className="gap-2">
                      <Label className={COMPOSER_FIELD_LABEL_CLASS}>
                        Credit mode
                      </Label>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={(value) => {
                            const mode = value as AuthoringMultiSelectScoring
                            field.onChange(mode)
                            form.setValue(
                              "unlimitedSelections",
                              resolveUnlimitedSelections(
                                mode,
                                form.getValues("unlimitedSelections"),
                              ),
                              { shouldDirty: true },
                            )
                          }}
                          className="grid gap-2"
                          disabled={disabled}
                        >
                          {AUTHORING_MULTI_SELECT_SCORING_OPTIONS.map(
                            (option) => {
                              const selected = field.value === option.value
                              return (
                                <Label
                                  key={option.value}
                                  htmlFor={`qb-score-${option.value}`}
                                  className={cn(
                                    "flex cursor-pointer items-start gap-3 rounded-2 border p-3 transition-colors",
                                    selected
                                      ? "border-brand bg-brand/10"
                                      : "border-border hover:bg-muted/40",
                                    disabled && "cursor-not-allowed opacity-60",
                                  )}
                                >
                                  <RadioGroupItem
                                    value={option.value}
                                    id={`qb-score-${option.value}`}
                                    className="mt-0.5"
                                    disabled={disabled}
                                  />
                                  <span className="min-w-0">
                                    <span className="block text-sm font-medium text-foreground">
                                      {option.label}
                                    </span>
                                    <span className="mt-0.5 block text-xs text-muted-foreground">
                                      {option.description}
                                    </span>
                                  </span>
                                </Label>
                              )
                            },
                          )}
                        </RadioGroup>
                      </FormControl>
                      <FormField
                        control={form.control}
                        name="unlimitedSelections"
                        render={({ field: unlimitedField }) => {
                          const locked = unlimitedPolicy !== "optional"
                          return (
                            <InspectorToggleRow
                              id="qb-unlimitedSelections"
                              variant="inset"
                              label="Allow unlimited selections"
                              description={
                                unlimitedPolicy === "forced_off"
                                  ? "Off for Partial credit. Students cannot select more options than there are correct answers."
                                  : unlimitedPolicy === "forced_on"
                                    ? "On for +/- Partial credit so wrong selections can be deducted."
                                    : "When on, students may select any number of options."
                              }
                              checked={unlimitedField.value}
                              onCheckedChange={(checked) => {
                                if (locked) return
                                unlimitedField.onChange(checked)
                              }}
                              disabled={disabled || locked}
                            />
                          )
                        }}
                      />
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
            ) : null}
            {visibleGeneralToggles.map((option) => (
              <FormField
                key={option.key}
                control={form.control}
                name={option.key}
                render={({ field }) => (
                  <InspectorToggleRow
                    id={`qb-${option.key}`}
                    variant="inset"
                    label={option.label}
                    description={option.description}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={disabled}
                  />
                )}
              />
            ))}
          </div>
        </InspectorSection>
        ) : null}

        <InspectorSection title="Classification">
          {/* One field per classification type, named and ordered as Settings › Classification has them. */}
          <div id="composer-classification" className="flex flex-col gap-4">
            {classificationTypes.map((type) => {
              if (type.scope === "content")
                return (
                  <FormField
                    key={type.scope}
                    control={form.control}
                    name="categoryIds"
                    render={({ field }) => (
                      <FormItem className="gap-1.5">
                        <FormControl>
                          <LeoApplyField anchor="category" revealBlobs>
                            <CategoryField
                              label={type.name}
                              value={field.value ?? []}
                              provenance={form.watch("categoryProvenance") ?? {}}
                              onChange={(ids, meta) => changeCategories(ids, meta)}
                              onOpen={() => openCategoryPicker("course-content")}
                              scopeIds={courseScopeIds}
                              courseName={course?.name}
                              blockedReason={
                                askForCourse && !course
                                  ? "Choose a course first. Its categories appear here."
                                  : undefined
                              }
                              disabled={disabled}
                            />
                          </LeoApplyField>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )
              if (type.scope === "standards")
                return (
                  <StandardsSummaryRow
                    key={type.scope}
                    value={composerCategoryIds ?? []}
                    onOpen={() => openCategoryPicker(STANDARDS_HUB_ID)}
                    courseTrees={courseOwnedTrees}
                    disabled={disabled}
                  />
                )
              if (type.scope === "difficulty")
                return (
                  <FormField
                    key={type.scope}
                    control={form.control}
                    name="difficulty"
                    render={({ field }) => (
                      <DifficultyField
                        label={type.name}
                        value={field.value}
                        onChange={(v) => {
                          field.onChange(v)
                          setDifficultyMode("manual")
                        }}
                        courseId={course?.id}
                      />
                    )}
                  />
                )
              if (type.scope === "bloom")
                return (
                  <BloomField
                    key={type.scope}
                    label={type.name}
                    value={form.watch("bloom")}
                    onChange={(v) =>
                      form.setValue("bloom", v, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    courseId={course?.id}
                  />
                )
              if (type.dimensionId) {
                // A hierarchy of the admin's: its categories ride with the question's others.
                const treeId = type.dimensionId
                return (
                  <CustomTreeField
                    key={type.scope}
                    label={type.name}
                    dimensionId={treeId}
                    value={composerCategoryIds ?? []}
                    onOpen={() => openCategoryPicker(treeId)}
                    disabled={disabled}
                  />
                )
              }
              return (
                <FormField
                  key={type.scope}
                  control={form.control}
                  name="classifications"
                  render={({ field }) => (
                    <CustomClassificationField
                      typeId={type.scope.slice("type:".length)}
                      value={field.value}
                      onChange={field.onChange}
                      courseId={course?.id}
                    />
                  )}
                />
              )
            })}
            {hideDiscrimination ? null : <DiscriminationReading insight={difficultyInsight} />}
          </div>
        </InspectorSection>
        <InspectorSection title="Tags">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="qb-tag-input" className={COMPOSER_FIELD_LABEL_CLASS}>
              Tags
            </Label>
            <ComposerTagsField
              id="qb-tag-input"
              tags={watchedTags}
              draft={tagDraft}
              disabled={disabled}
              onDraftChange={setTagDraft}
              onCommit={commitTag}
              onRemove={removeTag}
            />
          </div>
        </InspectorSection>
        <InspectorSection title="Creator and history">
          {!hasSavedHistory ? (
            <p className="m-0 text-sm text-muted-foreground">
              Not saved yet. The first version is recorded when you save this
              question.
            </p>
          ) : null}
          {hasSavedHistory && historyViewNumber != null &&
          historyViewNumber !== composeHistoryCurrentNumber ? (
            <LocalBanner
              variant="info"
              icon="fa-clock-rotate-left"
              action={{
                label: "Use the current version",
                onClick: restoreComposeCurrentVersion,
              }}
            >
              Showing version {historyViewNumber} in the editor. The current
              version is v{composeHistoryCurrentNumber}.
            </LocalBanner>
          ) : null}
          {hasSavedHistory ? (
          <CreatorHistoryCard
            author={historyCardVersion.author ?? composeHistoryBankRow.author}
            authorEmail={
              historyCardVersion.authorEmail ?? composeHistoryBankRow.authorEmail
            }
            updatedOn={composeHistoryPreview.updatedOn}
            versions={composeHistoryPreview.versions}
            selected={historyCardVersion}
            onOpenHistory={() => setHistoryPickerOpen(true)}
            title=""
            className="gap-0 [&>p:first-child]:hidden"
            coachMarkId="composer-version-history"
          />
          ) : null}
        </InspectorSection>
      </div>
      </InspectorSection>
    </fieldset>
  )
}

export function QuestionComposerInspector() {
  const { form, inspectorOpen, setInspectorOpen } = useQuestionComposerContext()
  const pageScroll = useQuestionComposerPageScroll()
  const inspectorSurface = useQuestionComposerInspectorSurface()

  if (inspectorSurface === "secondary-panel") {
    return null
  }

  const hideInspectorButton = (
    <Tip side="bottom" label="Collapse to icons">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => setInspectorOpen(false)}
        aria-label="Collapse to icons"
        aria-expanded={true}
      >
        <i className="fa-light fa-angles-right" aria-hidden="true" />
      </Button>
    </Tip>
  )

  return (
    <Form {...form}>
      {!inspectorOpen ? (
        <QuestionInspectorCompactNav density="card" />
      ) : (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ComposerPanelCard
            pageScrollBody={pageScroll}
            fillHeight
            headerActions={hideInspectorButton}
          >
            <QuestionComposerInspectorFields />
          </ComposerPanelCard>
        </div>
      )}
    </Form>
  )
}

